import { useEffect, useState } from 'react'
import { Box, Button, Typography, Modal, TextField, useMediaQuery, InputAdornment, IconButton } from '@mui/material'
import { Trash2 } from 'lucide-react'
import { getSocialLinks, addSocialLink, updateSocialLink, deleteSocialLink } from "../../../../../../services/api/SocialLinkDataService"
import { SocialLink } from '../../../../../../model/SocialLink'
import { isValidSocialValue } from '../../../../../../utils/socialIcons'
import { INVALID_URL_WARNING } from '../../../../../../utils/urlValidation'

const socials = [
    { id: 0, name: 'Email' },
    { id: 21, name: 'Website' },
    { id: 1, name: 'Instagram' },
    { id: 2, name: 'Facebook' },
    { id: 3, name: 'Twitter' },
    { id: 4, name: 'TikTok' },
    { id: 5, name: 'Pinterest' },
    { id: 6, name: 'Snapchat' },
    { id: 7, name: 'Reddit' },
    { id: 8, name: 'WhatsApp' },
    { id: 9, name: 'Tumblr' },
    { id: 10, name: 'YouTube' },
    { id: 11, name: 'Twitch' },
    { id: 12, name: 'Discord' },
    { id: 13, name: 'Spotify' },
    { id: 14, name: 'Amazon' },
    { id: 15, name: 'Ebay' },
    { id: 16, name: 'Etsy' },
    { id: 17, name: 'GitHub' },
    { id: 18, name: 'LinkedIn' },
    { id: 19, name: 'PayPal' },
    { id: 20, name: 'Vine' },
]

export function SocialLinkModal({ open, onClose, onAddIcon, pageId }) {
    const [urls, setUrls] = useState([])
    const [existingLinks, setExistingLinks] = useState([])
    const [errors, setErrors] = useState({})
    const isMobile = useMediaQuery('(max-width:1050px)')


    useEffect(() => {
        if (open) {
            fetchSocialLinks(pageId)
        } else {
            setUrls([])
            setErrors({})
        }
    }, [open])

    const fetchSocialLinks = async () => {
        try {
            const response = await getSocialLinks(pageId)
            if (response.length > 0) {
                setExistingLinks(response)
                setUrls(response)
            }
        } catch (error) {
            console.error('Failed to fetch social links:', error)
        }
    }

    const handleInputChange = (event, iconCode) => {
        const newUrls = urls.map((link) =>
            link.icon === iconCode ? { ...link, url: event.target.value } : link
        )

        // adding new social link if it doesn't exist
        if (!newUrls.some((link) => link.icon === iconCode)) {
            newUrls.push(new SocialLink("", event.target.value, iconCode, ""))
        }

        setUrls(newUrls)
        // Clear this field's error as the user edits it.
        setErrors((prev) => (prev[iconCode] ? { ...prev, [iconCode]: undefined } : prev))
    }

    const handleClearLink = (iconCode) => {
        // Clearing only empties the field; the actual delete happens on "Done"
        // (handleAddLinks removes existing links whose url is blank).
        setUrls((prev) =>
            prev.map((link) => (link.icon === iconCode ? { ...link, url: '' } : link))
        )
        setErrors((prev) => (prev[iconCode] ? { ...prev, [iconCode]: undefined } : prev))
    }

    const handleAddLinks = async () => {
        // Only allow real links — reject code snippets / non-URL input before any save call.
        const validationErrors = {}
        urls.forEach((link) => {
            if (link.url && link.url.trim() !== '' && !isValidSocialValue(link.icon, link.url)) {
                validationErrors[link.icon] = INVALID_URL_WARNING
            }
        })
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors)
            return
        }

        try {
            const promises = urls.map(async (link) => {
                const existingLink = existingLinks.find(
                    (existingLink) => existingLink.icon === link.icon
                )
                const hasUrl = link.url && link.url.trim() !== ''

                if (existingLink && !hasUrl) {
                    // Clearing a saved link removes it (the backend requires a non-blank url).
                    await deleteSocialLink(existingLink.id)
                } else if (existingLink && link.url !== existingLink.url) {
                    const socialLink = new SocialLink(existingLink.id, link.url, link.icon, pageId)
                    await updateSocialLink(socialLink)
                } else if (!existingLink && hasUrl) {
                    const socialLink = new SocialLink("", link.url, link.icon, pageId)
                    await addSocialLink(socialLink)
                }
            })

            await Promise.all(promises)
            // Surface only the links that still have a url to the caller's list.
            onAddIcon(urls.filter((link) => link.url && link.url.trim() !== ''))
            onClose()
        } catch (error) {
            console.error('Failed to save social links:', error)
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: isMobile ? 300 : 420,
                    bgcolor: 'background.paper',
                    borderRadius: '30px',
                    boxShadow: 24,
                    p: 4,
                }}
            >
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    Add Social Icon
                </Typography>
                <Box
                    sx={{
                        height: '60vh',
                        overflow: 'scroll',
                        gap: 1,
                    }}
                >
                    {socials.map((item) => {
                        const value = urls.find((link) => link.icon === item.id)?.url || ''
                        return (
                            <TextField
                                key={item.id}
                                margin="normal"
                                fullWidth
                                id={item.name}
                                label={item.name}
                                name={item.name}
                                autoComplete={item.name}
                                value={value}
                                placeholder={`Paste your ${item.name} link here`}
                                error={!!errors[item.id]}
                                helperText={errors[item.id] || ''}
                                onChange={(e) => handleInputChange(e, item.id)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={`Delete ${item.name} link`}
                                                edge="end"
                                                disabled={!value}
                                                onClick={() => handleClearLink(item.id)}
                                            >
                                                <Trash2 size={18} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )
                    })}
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddLinks}
                    fullWidth
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        mt: 2,
                        padding: '.6rem 1rem',
                        borderRadius: '40px',
                        boxShadow: "2px 3px 0px #000",
                        textTransform: 'none'
                    }}
                >
                    Done
                </Button>
            </Box>
        </Modal>
    )
}
